<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/RecipientsListHeader.vue -->

<script setup>
import { computed, ref, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  inboxOptions: {
    type: Array,
    required: true,
  },
  globalInbox: {
    type: String,
    default: '',
  },
  randomDistributionActive: {
    type: Boolean,
    default: false,
  },
  selectedDDI: {
    type: String,
    required: true,
  },
  countryOptions: {
    type: Array,
    required: true,
  },
  recipientsCount: {
    type: Number,
    default: 0,
  },
  recipients: {
    type: Array,
    default: () => [],
  },
  searchQuery: {
    type: String,
    default: '',
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  inboxSyncStatus: {
    type: Object,
    default: () => ({ type: 'none', message: '', color: 'slate' }),
  },
  hasMultipleInboxes: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'open-select-contacts',
  'update:global-inbox',
  'update:selected-d-d-i',
  'open-randomize-modal',
  'update:search-query',
  'delete-selected',
  'update-bulk-action',
  'update-all-recipients-inbox',
]);

// State
const showInboxDropdown = ref(false);

// Group inboxes by type
const groupedInboxOptions = computed(() => {
  const result = [];

  // Get all the inbox options
  const inboxes = props.inboxOptions || [];

  // Extract channel types for grouping
  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const messengerInboxes = [];
  const otherChannels = [];

  inboxes.forEach(inbox => {
    // Use name to identify the type since integration_type might not be available
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

    // Official API inboxes will have cloud_api or official in the name or a provider property
    if (
      inbox.integration_type === 'official' ||
      name.includes('api oficial') ||
      name.includes('cloud') ||
      name.includes('official')
    ) {
      whatsappOfficial.push({ ...inbox, integration_type: 'official' });
    }
    // Evolution inboxes will have evolution in the name
    else if (
      inbox.integration_type === 'evolution' ||
      name.includes('evolution') ||
      name.includes('unofficial')
    ) {
      whatsappEvolution.push({ ...inbox, integration_type: 'evolution' });
    }
    // Messenger inboxes
    else if (
      inbox.integration_type === 'messenger' ||
      inbox.channel_type === 'messenger' ||
      name.includes('messenger') ||
      name.includes('facebook')
    ) {
      messengerInboxes.push({ ...inbox, integration_type: 'messenger' });
    }
    // Otherwise, put it in other channels
    else {
      otherChannels.push(inbox);
    }
  });

  // Add evolution group
  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
    result.push(...whatsappEvolution);
  }

  // Add official group
  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
    result.push(...whatsappOfficial);
  }

  // Add messenger group
  if (messengerInboxes.length > 0) {
    result.push({ type: 'header', label: 'MESSENGER' });
    result.push(...messengerInboxes);
  }

  // Add other channels group
  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'OUTROS CANAIS' });
    result.push(...otherChannels);
  }

  return result;
});

// Detect inbox status - uniform, mixed, or empty
const inboxStatus = computed(() => {
  if (!props.recipients || props.recipients.length === 0) {
    return { status: 'empty' };
  }

  // Get all selectedInbox values from recipients
  const inboxIds = props.recipients
    .map(r => r.selectedInbox)
    .filter(id => id !== null && id !== undefined);

  if (inboxIds.length === 0) {
    return { status: 'empty' };
  }

  // Check if all are the same
  const firstInboxId = inboxIds[0];
  const allSame = inboxIds.every(id => String(id) === String(firstInboxId));

  if (allSame) {
    return { status: 'uniform', inboxId: firstInboxId };
  }

  return { status: 'mixed' };
});

// Get the selected inbox details
const selectedInboxDetails = computed(() => {
  if (props.randomDistributionActive && props.recipientsCount) {
    return {
      name: 'Aleatório',
      type: 'random',
    };
  }

  // Check inbox status first
  if (inboxStatus.value.status === 'mixed') {
    return {
      name: 'Vários inboxes',
      type: 'mixed',
    };
  }

  // If uniform, use the common inbox
  if (inboxStatus.value.status === 'uniform' && inboxStatus.value.inboxId) {
    const inbox = props.inboxOptions.find(inbox => {
      const inboxId = String(inbox.id || inbox.value);
      return inboxId === String(inboxStatus.value.inboxId);
    });

    if (inbox) {
      return {
        id: inbox.id || inbox.value,
        name: getInboxDisplayName(inbox),
        channel_type: inbox.channel_type,
        integration_type: inbox.integration_type,
        provider: inbox.provider,
      };
    }
  }

  // Fall back to globalInbox
  if (!props.globalInbox) return null;

  const inbox = props.inboxOptions.find(inbox => {
    const inboxId = String(inbox.id || inbox.value);
    const globalId = String(props.globalInbox);
    const match = inboxId === globalId;
    return match;
  });

  if (!inbox) {
    return null;
  }

  return {
    id: inbox.id || inbox.value,
    name: getInboxDisplayName(inbox),
    channel_type: inbox.channel_type,
    integration_type: inbox.integration_type,
    provider: inbox.provider,
  };
});

const bulkActions = computed(() => {
  return [
    { id: 'delete', name: 'Deletar' },
    ...props.inboxOptions.map(inbox => ({
      id: `inbox-${inbox.value || inbox.id}`,
      name: `Caixa de entrada: ${inbox.inbox_name || inbox.name}`,
    })),
  ];
});

const handleBulkAction = action => {
  if (!props.selectedRows.length) return;
  emit('update-bulk-action', action);
};

const openSelectContacts = () => {
  emit('open-select-contacts');
};

const updateGlobalInbox = value => {
  emit('update:global-inbox', value);
};

const updateSelectedDDI = value => {
  emit('update:selected-d-d-i', value);
};

const openRandomizeModal = () => {
  emit('open-randomize-modal');
};

const updateSearchQuery = value => {
  emit('update:search-query', value);
};

const deleteSelectedRecipients = () => {
  emit('delete-selected');
};

// Toggle dropdown
const toggleInboxDropdown = () => {
  showInboxDropdown.value = !showInboxDropdown.value;
};

// Handle inbox selection from dropdown with logging
const handleInboxSelection = (item) => {
  const inboxId = Number(item.id || item.value);

  console.log('[RecipientsListHeader] inbox selected', {
    inboxId,
    integration_type: item.integration_type,
    channel_type: item.channel_type,
    name: item.inbox_name || item.name || item.display_name,
  });

  emit('update-all-recipients-inbox', inboxId);
  updateGlobalInbox(String(item.id || item.value));
  toggleInboxDropdown();
};

// Helper function to get display name with profile support
const getInboxDisplayName = (inbox) => {
  if (!inbox) return '';

  // For messenger, use display_name first, then profile (page name), then fallback
  if (inbox.integration_type === 'messenger') {
    return inbox.display_name || inbox.profile || inbox.inbox_name || inbox.name || '';
  }

  // For all other types, use standard fallback chain
  return inbox.display_name || inbox.inbox_name || inbox.name || '';
};

// Determine the icon class with color based on inbox type
const getInboxIconWithColor = inbox => {
  if (!inbox) return 'i-lucide-message-square text-n-slate-11';

  if (inbox.type === 'random') {
    return 'i-lucide-shuffle text-n-iris-11 dark:text-n-iris-11';
  }

  if (inbox.type === 'mixed') {
    return 'i-lucide-git-merge text-n-slate-11 dark:text-n-slate-11';
  }

  if (inbox.integration_type === 'official') {
    return 'i-ri-whatsapp-fill text-grass-600 dark:text-grass-500';
  }

  if (inbox.integration_type === 'evolution') {
    return 'i-lucide-message-circle text-grass-600 dark:text-grass-500';
  }

  if (inbox.integration_type === 'messenger') {
    return 'i-ri-messenger-fill text-n-brand dark:text-n-brand';
  }

  return 'i-lucide-message-square text-n-slate-11';
};

// Close dropdown when clicking outside
const handleClickOutside = event => {
  if (!event.target.closest('.inbox-dropdown') && showInboxDropdown.value) {
    showInboxDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="space-y-4">
    <!-- Main Card with Controls -->
    <div class="shadow rounded-xl bg-n-solid-1 border-ash-600 p-3 sm:p-4 overflow-visible">
      <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <!-- "Add Contacts to List" button -->
        <Button
          label="Adicionar Contatos"
          icon="i-lucide-user-plus"
          variant="solid"
          color="blue"
          size="md"
          class="rounded-lg w-full sm:w-auto"
          @click="openSelectContacts"
        />

        <!-- Search Bar -->
        <div class="w-full sm:flex-1 sm:min-w-[200px]">
          <div class="relative flex flex-col min-w-0">
            <span
              class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
            <input
              :value="searchQuery"
              type="text"
              class="h-10 ltr:!pl-8 !py-2 rtl:!pr-8 border border-n-slate-3 dark:border-n-slate-6 hover:border-n-slate-6 dark:hover:border-n-slate-7 focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm !px-3 !mb-0 rounded-lg bg-n-background dark:bg-n-slate-3 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-200 ease-in-out"
              placeholder="Buscar contatos..."
              @input="updateSearchQuery($event.target.value)"
            />
          </div>
        </div>
        <!-- Inbox Selector with Groups -->
        <div class="relative inbox-dropdown w-full sm:w-auto">
          <button
            class="h-10 px-3 border border-n-slate-3 dark:border-n-slate-6 rounded-lg bg-n-background dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-1 dark:hover:bg-n-slate-4 focus:border-n-brand focus:outline-none flex justify-between items-center w-full sm:min-w-[200px] transition-all duration-200"
            @click.stop="toggleInboxDropdown"
          >
            <div class="flex items-center overflow-hidden">
              <!-- Inbox icon without background -->
              <i
                v-if="selectedInboxDetails"
                :class="getInboxIconWithColor(selectedInboxDetails) + ' text-xl mr-2 flex-shrink-0'"
              />

              <span class="truncate text-sm">
                <span v-if="randomDistributionActive && recipientsCount">
                  Aleatório
                </span>
                <span v-else-if="selectedInboxDetails">
                  {{ selectedInboxDetails.name }}
                </span>
                <span v-else> Selecione uma caixa </span>
              </span>
            </div>
            <i
              class="i-lucide-chevron-down ml-2 flex-shrink-0"
              :class="{ 'transform rotate-180': showInboxDropdown }"
            />
          </button>

          <!-- Dropdown Options -->
          <div
            v-if="showInboxDropdown"
            class="absolute z-50 top-full left-0 mt-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg shadow-lg bg-white dark:bg-n-slate-2 overflow-hidden min-w-[250px]"
          >
            <!-- Grouped Inboxes -->
            <div class="max-h-60 overflow-y-auto">
              <template
                v-for="(item, index) in groupedInboxOptions"
                :key="index"
              >
                <!-- Group Header -->
                <div
                  v-if="item.type === 'header'"
                  class="px-3 py-1 text-xs font-semibold bg-n-slate-1 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 border-y border-n-slate-3 dark:border-n-slate-6 sticky top-0 z-10"
                >
                  {{ item.label }}
                </div>

                <!-- Inbox Option -->
                <div
                  v-else
                  class="p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-4 cursor-pointer"
                  @click="handleInboxSelection(item)"
                >
                  <div class="flex items-center">
                    <!-- Icon without background -->
                    <i
                      :class="getInboxIconWithColor(item) + ' text-xl mr-3'"
                    />

                    <div class="flex-1">
                      <div class="text-sm font-medium">
                        {{ getInboxDisplayName(item) }}
                      </div>
                      <div
                        v-if="item.integration_type === 'official'"
                        class="text-xs text-green-700"
                      >
                        API Oficial
                      </div>
                      <div
                        v-else-if="item.integration_type === 'evolution'"
                        class="text-xs text-green-700"
                      >
                        Não Oficial
                      </div>
                      <div
                        v-else-if="item.integration_type === 'messenger'"
                        class="text-xs text-n-brand dark:text-n-brand"
                      >
                        Messenger
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Randomize Button -->
        <div class="relative group self-end sm:self-auto sm:ml-auto">
          <button
            aria-label="Aleatório"
            class="h-10 px-3 border border-n-slate-3 dark:border-n-slate-6 rounded-lg text-sm font-medium text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-1 dark:hover:bg-n-slate-4 focus:outline-none focus:border-n-brand disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
            :class="{
              'bg-n-iris-2 dark:bg-n-iris-3 !border-n-iris-9 dark:!border-n-iris-10 hover:bg-n-iris-3 dark:hover:bg-n-iris-4':
                randomDistributionActive,
              'bg-n-background dark:bg-n-slate-3': !randomDistributionActive
            }"
            :disabled="!recipientsCount"
            @click="openRandomizeModal"
          >
            <i class="i-lucide-shuffle" />
            <span class="hidden sm:inline">Aleatório</span>
          </button>

          <!-- Tooltip -->
          <div
            class="absolute bottom-full mb-2 right-0 bg-n-slate-12 dark:bg-n-slate-1 text-white text-xs p-2 rounded-lg shadow-lg invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap"
          >
            Distribuição aleatória por número
          </div>
        </div>
      </div>
      
      <!-- Second Row: Contact counts and actions -->
      <div class="flex items-center justify-between pt-3 mt-3 border-t border-n-slate-3 dark:border-n-slate-4">
        <!-- Left side: Contact counts -->
        <div class="flex items-center gap-4">
          <div class="flex items-center">
            <i class="i-lucide-users text-n-slate-11 mr-2" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ recipientsCount }} contatos
            </span>
          </div>
          <div
            v-if="selectedRows.length > 0"
            class="flex items-center"
          >
            <i class="i-lucide-check-square text-n-blue-11 mr-2" />
            <span class="text-sm font-medium text-n-blue-11">
              {{ selectedRows.length }} selecionados
            </span>
          </div>
        </div>
        
        <!-- Right side: Actions -->
        <div class="flex items-center">
          <!-- Delete Selected Button (only show when items are selected) -->
          <Button
            v-if="selectedRows.length > 0"
            label="Excluir Selecionados"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash-2"
            size="sm"
            @click="deleteSelectedRecipients"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Optional: Add some animation for dropdown */
.inbox-dropdown .max-h-60 {
  transition: max-height 0.3s ease-in-out;
}

/* Add the pulsing animation for the info icon */
@keyframes pulse {
  0% {
    opacity: 0.7;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.1);
  }

  100% {
    opacity: 0.7;
    transform: scale(1);
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}
</style>
